/**
 * VAPT Security Utility Functions
 * Shared across all CRM modules for consistent security enforcement
 *
 * Security Features:
 * - XSS Prevention (Input sanitization)
 * - URL Validation
 * - File Upload Security (Type, Size, Name validation)
 * - Email Validation
 * - Phone Number Sanitization
 * - Numeric Input Validation
 *
 * OWASP Top 10 2021 Compliance
 */

/**
 * Sanitize input to prevent XSS attacks while preserving valid text characters
 * @param {string} input - User input string
 * @returns {string} - Sanitized string
 */
export const sanitizeInput = (input) => {
  if (input === null || input === undefined) return "";
  if (typeof input !== "string") return String(input);
  let str = input;
  if (str.includes("&")) {
    try {
      const el = document.createElement("textarea");
      el.innerHTML = str;
      str = el.value;
    } catch {
      // fallback
    }
  }
  return str.slice(0, 5000); // Limit input length to prevent DoS
};

/**
 * Sanitize textarea input to prevent XSS while preserving spaces and valid characters
 * @param {string} input - User textarea input
 * @returns {string} - Sanitized string
 */
export const sanitizeTextarea = (input) => {
  if (input === null || input === undefined) return "";
  if (typeof input !== "string") return String(input);
  let str = input;
  if (str.includes("&")) {
    try {
      const el = document.createElement("textarea");
      el.innerHTML = str;
      str = el.value;
    } catch {
      // fallback
    }
  }
  return str.slice(0, 5000);
};

/**
 * Validate URL format and protocol
 * @param {string} url - URL to validate
 * @returns {boolean} - True if valid URL
 */
export const validateUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  try {
    const parsedUrl = new URL(url.startsWith("http") ? url : `https://${url}`);
    // Only allow http and https protocols
    return ["http:", "https:"].includes(parsedUrl.protocol);
  } catch {
    return false;
  }
};

/**
 * Sanitize and validate email
 * @param {string} email - Email to sanitize
 * @returns {string} - Sanitized email
 */
export const sanitizeEmail = (email) => {
  if (!email || typeof email !== "string") return "";
  const trimmed = email.trim().toLowerCase();
  // Basic email validation pattern
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(trimmed) ? trimmed : "";
};

/**
 * Sanitize phone number
 * @param {string} phone - Phone number to sanitize
 * @returns {string} - Sanitized phone number
 */
export const sanitizePhoneNumber = (phone) => {
  if (!phone || typeof phone !== "string") return "";
  // Remove all non-numeric characters except + and spaces
  return phone.replace(/[^0-9+\s-]/g, "").slice(0, 20);
};

/**
 * Sanitize numeric input with optional max value - allows decimal points
 * @param {string|number} input - Numeric input (can include decimal, e.g., 10.5, 1234.50)
 * @param {number} max - Maximum allowed value
 * @returns {string} - Sanitized numeric string with decimals preserved
 */
export const sanitizeNumericInput = (input, max = 999999999) => {
  if (!input && input !== 0) return 0;
  let numStr = String(input).replace(/[^0-9.]/g, "");

  if (numStr === "") return "";

  // Ensure only one decimal point (keep first, remove others)
  const parts = numStr.split(".");
  if (parts.length > 2) {
    numStr = parts[0] + "." + parts.slice(1).join("");
  }

  // Prevent leading zeros before decimal (01.5 -> 1.5), but allow "0.x"
  if (numStr.match(/^0\d/)) {
    numStr = numStr.replace(/^0+/, "");
    if (!numStr.startsWith(".")) {
      // no-op, numStr is already adjusted
    }
  }

  const num = parseFloat(numStr);
  if (isNaN(num)) return "";

  // If input is still being typed (ends with . or incomplete), return as-is
  if (numStr.endsWith(".")) {
    return numStr;
  }

  // Otherwise, clamp to max and return
  const clamped = Math.min(num, max);
  return clamped.toString();
};

/**
 * Validate file type against allowed MIME types
 * @param {File|Object} file - File object or object with type property
 * @param {Array<string>} allowedTypes - Array of allowed MIME types
 * @returns {boolean} - True if file type is allowed
 */
export const validateFileType = (file, allowedTypes = []) => {
  if (!file) return false;

  // Handle File object
  if (file.type) {
    return allowedTypes.some((type) => {
      if (type.endsWith("/*")) {
        return file.type.startsWith(type.replace("/*", "/"));
      }
      return file.type === type;
    });
  }

  // Handle base64 string
  if (typeof file === "string" && file.includes(",")) {
    const mimeType = file.split(",")[0].split(":")[1].split(";")[0];
    return allowedTypes.length === 0 || allowedTypes.includes(mimeType);
  }

  return false;
};

/**
 * Validate file size
 * @param {File|string} file - File object or base64 string
 * @param {number} maxSizeMB - Maximum size in MB
 * @returns {boolean} - True if file size is within limit
 */
export const validateFileSize = (file, maxSizeMB = 10) => {
  if (!file) return false;

  // Handle File object
  if (file.size) {
    return file.size <= maxSizeMB * 1024 * 1024;
  }

  // Handle base64 string
  if (typeof file === "string") {
    const sizeInBytes = (file.length * 3) / 4;
    const sizeInMB = sizeInBytes / (1024 * 1024);
    return sizeInMB <= maxSizeMB;
  }

  return false;
};

/**
 * Sanitize filename to prevent path traversal attacks
 * @param {string} fileName - Original filename
 * @returns {string} - Sanitized filename
 */
export const sanitizeFileName = (fileName) => {
  if (!fileName || typeof fileName !== "string") return "download";
  // Normalize legacy MIME-type based file extension artifacts
  const cleanName = fileName
    .replace(/\.vnd\.openxmlformats-officedocument\.spreadsheetml\.sheet$/i, ".xlsx")
    .replace(/\.vnd\.ms-excel$/i, ".xls")
    .replace(/\.vnd\.openxmlformats-officedocument\.wordprocessingml\.document$/i, ".docx")
    .replace(/\.msword$/i, ".doc");

  // Remove path traversal attempts and dangerous characters
  return cleanName
    .replace(/[\/\\:*?"<>|]/g, "_")
    .replace(/^\.+/, "")
    .slice(0, 255);
};

/**
 * Download blob with security validation
 * @param {string} url - Blob URL, data URL, or HTTP URL
 * @param {string} fileName - Filename for download
 * @param {number} maxSizeMB - Maximum blob size in MB
 * @returns {Promise<boolean>} - True if download initiated successfully
 */
export const downloadBlobFile = async (url, fileName, maxSizeMB = 100) => {
  try {
    // Validate URL
    if (!url || typeof url !== "string") {
      return false;
    }

    // Sanitize filename
    const safeName = sanitizeFileName(fileName);
    if (!safeName) {
      return false;
    }

    // Direct browser anchor download helper (without target="_blank" to prevent opening in a new tab)
    const triggerDirectDownload = (targetUrl, downloadName) => {
      try {
        const link = document.createElement("a");
        link.href = targetUrl;
        if (downloadName) {
          link.setAttribute("download", downloadName);
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return true;
      } catch {
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = targetUrl;
        document.body.appendChild(iframe);
        setTimeout(() => {
          try {
            document.body.removeChild(iframe);
          } catch {}
        }, 60000);
        return true;
      }
    };

    // If it's an HTTP URL, attempt fetch with blob, fallback to direct download on CORS
    if (url.startsWith("http://") || url.startsWith("https://")) {
      if (!validateUrl(url)) {
        return false;
      }

      try {
        const response = await fetch(url);
        if (response.ok) {
          const blob = await response.blob();
          if (blob.size <= maxSizeMB * 1024 * 1024) {
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = safeName;
            link.rel = "noopener noreferrer";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
            return true;
          }
        }
      } catch {
        // Fetch failed due to CORS or network on S3 presigned URL - fallback to direct download
        return triggerDirectDownload(url, safeName);
      }

      return triggerDirectDownload(url, safeName);
    }
    // For data URLs, validate size
    else if (url.startsWith("data:")) {
      const base64Data = url.split(",")[1] || url;
      const sizeInBytes = (base64Data.length * 3) / 4;
      const sizeInMB = sizeInBytes / (1024 * 1024);

      if (sizeInMB > maxSizeMB) {
        return false;
      }
      return triggerDirectDownload(url, safeName);
    }
    // For blob URLs
    else if (url.startsWith("blob:")) {
      return triggerDirectDownload(url, safeName);
    }

    return false;
  } catch {
    if (url && (url.startsWith("http://") || url.startsWith("https://"))) {
      try {
        window.open(url, "_blank", "noopener,noreferrer");
        return true;
      } catch {
        return false;
      }
    }
    return false;
  }
};
